<template>
  <div class="allocation-details-container">
    <!-- Header Card -->
    <a-skeleton :loading="loading && Object.keys(allocationDetails).length === 0" active>
      <a-card :loading="loading && Object.keys(allocationDetails).length === 0" class="allocation-header-card">
        <div class="header-content">
          <div>
            <h2 class="header-title">Mark as Processed – Allocation</h2>
            <!-- <div class="header-subtext">
              For: {{ allocationDetails.distributionCenter || "—" }}
            </div> -->
          </div>
          <div>
            <StatusTag :status="allocationDetails.status" />
          </div>
        </div>
        <a-row :gutter="24" class="header-row">
          <a-col :span="6">
            <div class="label">Allocation ID</div>
            <div class="value">
              {{ allocationDetails.allocationId || "N/A" }}
            </div>
          </a-col>
          <a-col :span="6">
            <div class="label">Request Date</div>
            <div class="value">
              {{ formatDate(allocationDetails.submissionDate) }}
            </div>
          </a-col>
          <a-col :span="6">
            <div class="label">Source Warehouse</div>
            <div class="value">
              {{ allocationDetails.distributionCenter || "—" }}
            </div>
          </a-col>
          <a-col :span="6">
            <div class="label">Seller</div>
            <div class="value">{{ sellerName || "—" }}</div>
          </a-col>
        </a-row>
        <a-row :gutter="24" class="header-row">
          <a-col :span="6">
            <div class="label">Allocated By</div>
            <div class="value">{{ allocationDetails.requestedBy || "—" }}</div>
          </a-col>
          <!-- <a-col :span="6">
            <div class="label">Assigned Receiver</div>
            <div class="value receiver">
              <a-avatar style="background-color: #26a246; margin-right: 8px">
                {{ getInitials(allocationDetails.assignedReceiver) }}
              </a-avatar>
              {{ allocationDetails.assignedReceiver || "—" }}
            </div>
          </a-col> -->
          <a-col :span="6">
            <div class="label">Contact</div>
            <div class="value" style="color: #26a246">
              {{ sellerContact || "—" }}
            </div>
          </a-col>
          <a-col :span="6">
            <div class="label">Processed Date</div>
            <div class="value">—</div>
          </a-col>
        </a-row>
      </a-card>
    </a-skeleton>

    <!-- Allocated Products Table -->
    <a-skeleton :loading="loading && Object.keys(allocationDetails).length === 0" active>
      <a-card class="products-card" style="margin-top: 24px">
        <a-alert
          v-if="showBatchAlert"
          type="warning"
          show-icon
          message="All batches must be marked as 'Received' before the allocation can be processed."
          style="margin-bottom: 16px"
        />
        <div class="table-header">
          <span class="table-title">Allocated Products</span>
          <span class="consignment-id" v-if="allocationDetails.consignmentId">
            Consignment ID:
            <a
              style="color: #26a246; cursor: pointer"
              @click="goToConsignmentDetails"
              >{{ allocationDetails.consignmentId }}</a
            >
          </span>
        </div>
        <a-table
          :columns="productColumns"
          :data-source="allocationDetails.allocatedBatches || []"
          :pagination="false"
          rowKey="batchId"
          bordered
          :scroll="{ x: 'max-content' }"
        >
          <template slot="receivedQty" slot-scope="text, record">
            <span v-if="record.status === 'Received'">
              {{ record.receivedQuantity }}
            </span>
            <a-input
              v-else
              v-model="record.receivedQuantity"
              placeholder="Enter qty"
              style="width: 120px; margin-right: 10px"
              min="1"
              type="number"
              :disabled="
                record.status && record.status.toLowerCase() === 'received'
              "
            />
          </template>
          <template slot="action" slot-scope="text, record">
            <StatusTag
              v-if="record.status && record.status.toLowerCase() === 'received'"
              status="Marked as Received"
            />
            <a-button
              v-else
              type="primary"
              style="background: #26a246; color: white"
              @click="markBatchAsReceived(record)"
              :loading="loadingBatchId === record.batchId"
              :disabled="
                loadingBatchId === record.batchId ||
                !isValidReceivedQuantity(record.receivedQuantity)
              "
              icon="check"
              >Mark as received
            </a-button>
          </template>
          <template slot="allocationStatus" slot-scope="text, record">
            <StatusTag :status="record.status" />
          </template>
          <template slot="status" slot-scope="text, record">
            <StatusTag :status="record.batchOrder.status" />
          </template>
          <template slot="expiryDate" slot-scope="text, record">
            {{ formatDate(record.batchOrder.expiryDate) }}
          </template>
          <!-- <template slot="dateDelivered" slot-scope="text, record">
            {{ record.dateDelivered ? formatDate(record.dateDelivered) : "—" }}
          </template> -->
          <template slot="storageCondition" slot-scope="text, record">
            <StatusTag :status="record.batchOrder.storageCondition" />
          </template>
        </a-table>
      </a-card>
    </a-skeleton>

    <!-- Footer Actions -->
    <div class="footer-actions">
      <a-button @click="goBack" class="footer-btn">Back</a-button>
      <!-- <a-button
        type="primary"
        class="footer-btn"
        style="background-color: #26a246; border-color: #26a246"
        @click="markAsProcessed"
      >
        <a-icon type="check" /> Mark as Processed
      </a-button> -->
    </div>
  </div>
</template>

<script>
import sellersService from "@/api/sellers.service";
import { mapGetters, mapActions } from "vuex";
import { formatDate, getInitials } from "../../../utils";
import StatusTag from "../../shared-components/StatusTag.vue";

export default {
  name: "AllocationDetails",
  components: { StatusTag },
  props: {
    userId: { type: String, required: true },
    allocationId: { type: String, required: true },
  },
  data() {
    return {
      loading: true,
      allocationDetails: {},
      productColumns: [
        { title: "Batch Number", dataIndex: "batchOrder.batchNumber", key: "batchNumber" },
        { title: "Product Name", dataIndex: "batchOrder.productName", key: "productName" },
        {
          title: "Expiry Date",
          key: "expiryDate",
          scopedSlots: { customRender: "expiryDate" },
        },
        {
          title: "Stock Requested",
          dataIndex: "requestedQuantity",
          key: "requestedQuantity",
        },
        {
          title: "Received Qty Allocated",
          key: "receivedQty",
          scopedSlots: { customRender: "receivedQty" },
        },
        {
          title: "Action",
          key: "action",
          scopedSlots: { customRender: "action" },
        },
        {
          title: "Status",
          key: "allocationStatus",
          scopedSlots: { customRender: "allocationStatus" },
        },
        {
          title: "Health Status",
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: "Storage Type",
          key: "storageCondition",
          scopedSlots: { customRender: "storageCondition" },
        },
        // {
        //   title: "Date Delivered",
        //   key: "dateDelivered",
        //   scopedSlots: { customRender: "dateDelivered" },
        // },
      ],
      loadingBatchId: null,
    };
  },
  computed: {
    ...mapGetters("sellerManagement", ["seller"]),
    sellerName() {
      if (!this.seller) return "N/A";
      if (
        this.seller.businessDetails &&
        this.seller.businessDetails.businessName
      ) {
        return this.seller.businessDetails.businessName;
      } else if (this.seller.sellerName) {
        return this.seller.sellerName;
      }
      return "N/A";
    },
    sellerContact() {
      return (
        (this.seller &&
          this.seller.accountDetails &&
          this.seller.accountDetails.phoneNumber) ||
        "N/A"
      );
    },
    allBatchesReceived() {
      const batches = this.allocationDetails.allocatedBatches || [];
      return (
        batches.length > 0 &&
        batches.every(
          (batch) => batch.status && batch.status.toLowerCase() === "received"
        )
      );
    },
    showBatchAlert() {
      const batches = this.allocationDetails.allocatedBatches || [];
      return batches.length > 1 && !this.allBatchesReceived;
    },
  },
  mounted() {
    this.fetchSeller(this.userId);
    this.fetchAllocationDetails();
  },
  methods: {
    ...mapActions("sellerManagement", ["fetchSeller"]),
    async fetchAllocationDetails() {
      this.loading = true;
      const response = await sellersService.fetchSellerAllocationDetails(
        this.userId,
        this.allocationId
      );
      if (response && response.status === 200) {
        this.allocationDetails = response?.data?.data || {};
        console.log("allocdetails", response.data);
      } else {
        this.$message.error("Failed to fetch allocation details.");
      }
      this.loading = false;
    },
    formatDate,
    getInitials,
    async markBatchAsReceived(record) {
      if (!record.receivedQuantity || isNaN(record.receivedQuantity)) {
        this.$message.warning("Please enter a valid received quantity.");
        return;
      }

      if (Number(record.receivedQuantity) > Number(record.requestedQuantity)) {
        this.$message.warning("Received quantity cannot be greater than requested quantity.");
        return;
      }
      this.loadingBatchId = record.batchId;
      try {
        const payload = {
          allocationId: this.allocationDetails.id,
          batches: [
            {
              batchOrderId: record.batchOrder.id,
              quantityReceived: Number(record.receivedQuantity),
            },
          ],
        };
        const response = await sellersService.updateAllocationBatchStatus(
          payload
        );
        console.log(response);
        if (response.data.code === 200) {
          this.$message.success("Batch marked as received!");
          await this.fetchAllocationDetails();
        } else {
          this.$message.error(
            "Failed to update batch status: " +
              (response.data.message || "Unknown error.")
          );
        }
      } finally {
        this.loadingBatchId = null;
      }
    },
    async markAsProcessed() {
      // Only allow if all batches are received
      const allReceived = (this.allocationDetails.allocatedBatches || []).every(
        (batch) => batch.status && batch.status.toLowerCase() === "received"
      );
      if (!allReceived) {
        this.$message.warning(
          "All batches must be marked as received before processing."
        );
        return;
      }
      this.loading = true;
      try {
        const payload = {
          allocationId: this.allocationId,
          status: "Processed",
        };
        // If you add a sellersService method for this, use it here. Otherwise, keep axios call.
        const response = await sellersService.markAllocationProcessed(payload);
        if (response.data.code === 200) {
          this.$message.success("Allocation marked as processed!");
          this.goBack();
        } else {
          this.$message.error(
            "Failed to mark allocation as processed: " +
              (response.data.message || "Unknown error.")
          );
        }
      } catch (error) {
        this.$message.error("Failed to mark allocation as processed.");
      } finally {
        this.loading = false;
      }
    },
    goBack() {
      this.$router.push(`/sellers/${this.userId}?tab=6&subTab=3`);
    },
    async goToConsignmentDetails() {
      if (!this.allocationDetails.consignmentId) {
        this.$message.warning("Consignment ID not found for this allocation.");
        return;
      }

      this.$router.push({
        name: "ConsignmentDetails",
        params: {
          userId: this.userId,
          consignmentId: this.allocationDetails.id,
        },
      });
    },
    isValidReceivedQuantity(value) {
      const num = Number(value);
      return Number.isInteger(num) && num > 0;
    },
  },
};
</script>

<style scoped>
.allocation-details-container {
  padding: 24px;
  background-color: #f8f9fa;
  min-height: 100vh;
}
.allocation-header-card {
  margin-bottom: 24px;
  border-radius: 12px;
}
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
}
.header-title {
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 4px;
}
.header-subtext {
  color: #6b7280;
  font-size: 15px;
  margin-bottom: 0;
}
.header-row {
  margin-bottom: 0;
  margin-top: 8px;
}
.label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}
.value {
  font-size: 16px;
  color: #222;
  font-weight: 600;
}
.receiver {
  display: flex;
  align-items: center;
}
.products-card {
  border-radius: 12px;
}
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.table-title {
  font-size: 18px;
  font-weight: bold;
}
.consignment-id {
  font-size: 14px;
  font-weight: 500;
}
.footer-actions {
  margin-top: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.footer-btn {
  min-width: 120px;
  background-color: #26a246;
  color: #fff;
  border-color: #26a246;
  font-weight: 600;
}
</style>
